import { useState, type ReactNode } from 'react'
import { CircleCheck, MoreHorizontal } from 'lucide-react'
import { PhotoType } from '@numeo/types-core'
import { Button } from '@/components/ds2'
import { RevisionCard } from '../uploads/RevisionCard'
import { UploadButton } from '../uploads/UploadButton'
import type { RevisionChoice } from '../uploads/uploadFlow'
import type { SlotAction } from './LoadRow'
import { DONE_LABEL, type RowAction } from './rowActions'
import { SetRateAction } from './SetRateAction'
import { useInvoiceReview } from '../invoicing/review/reviewStore'
import { useRowRevision } from './useRowRevision'

interface LoadFixActionsProps {
    loadNumber: string
    /** The load's id when a card named it, so an upload goes to exactly this load. */
    loadId?: string
    actions: RowAction[]
    onSend: (message: string) => void
    /** The row's invoice action, when it has one: it takes the action column's first place and the fixes wait behind "⋯". */
    lead?: ReactNode
    /** Controlled popover state (a row opens it from its document slot); uncontrolled when omitted. */
    open?: boolean
    onOpenChange?: (open: boolean) => void
    /** False when the action column keeps no fix of its own: every fix waits behind "⋯" (an invoice row, whose documents open from its chips). */
    firstInColumn?: boolean
}

/** An action the user did here, in its place. */
const Done = ({ label }: { label: string }) => (
    <span role="status" className="flex items-center gap-1 py-1 text-xs text-success">
        <CircleCheck className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {label}
    </span>
)

/**
 * The actions that fix one load, right in the chat (never a board link: an upload made there would not reach the chat). The row's one
 * action sits in its action column; the rest, and a rate con revision to review, open from "⋯" in a small panel over the row, so no
 * button line hangs under the row (design §3). An action the user did shows as done in its place. "Review POD" opens the load's
 * invoice review drawer, the page's one right-hand panel. An invoice is not a fix: its row asks for approval itself, so it is never offered here.
 */
export const LoadFixActions = ({ loadNumber, loadId, actions: offered, onSend, lead, open, onOpenChange, firstInColumn = true }: LoadFixActionsProps) => {
    const rowRevision = useRowRevision()
    const openReview = useInvoiceReview((state) => state.open)
    const [done, setDone] = useState<ReadonlySet<RowAction['kind']>>(new Set())
    const [ownMenu, setOwnMenu] = useState(false)
    const menu = open ?? ownMenu
    const setMenu = (next: boolean) => {
        if (open === undefined) setOwnMenu(next)
        onOpenChange?.(next)
    }
    const markDone = (kind: RowAction['kind']) => setDone((known) => new Set(known).add(kind))
    const actions = offered.filter((step) => step.kind !== 'invoice')
    // The action column shows the first fix; while the popover is open it lists every fix, so the column's one is not repeated.
    const revisionOpen = rowRevision.revision !== null
    const panelOpen = revisionOpen || menu
    const primary = lead || panelOpen || !firstInColumn ? undefined : actions[0]
    // Every fix not in the column waits behind "⋯", including the one fix left when the column holds a lead.
    const behindMenu = actions.length - (primary ? 1 : 0)
    // A revision the user accepted or kept ends the upload; one already resolved elsewhere or left pending does not.
    const resolveRevision = (choice: RevisionChoice) => {
        rowRevision.resolve(choice)
        if (choice.kind === 'accepted' || choice.kind === 'kept') markDone('upload_ratecon')
    }
    // Nothing to show: no fix, no invoice and no open panel. An open "⋯" panel always shows, even when the row's first fix is hidden in it.
    if (!lead && actions.length === 0 && !panelOpen) return null

    const action = (step: RowAction) => {
        if (done.has(step.kind)) return <Done label={DONE_LABEL[step.kind]} />
        switch (step.kind) {
            case 'upload_ratecon':
                return (
                    <UploadButton
                        loadNumber={loadNumber}
                        loadId={loadId}
                        type={PhotoType.RATECON}
                        label={step.label}
                        onRevision={rowRevision.open}
                        onUploaded={() => markDone('upload_ratecon')}
                    />
                )
            case 'upload_pod':
                return (
                    <UploadButton loadNumber={loadNumber} loadId={loadId} type={PhotoType.POD} label={step.label} onUploaded={() => markDone('upload_pod')} />
                )
            case 'set_rate':
                return (
                    <SetRateAction
                        loadNumber={loadNumber}
                        label={step.label}
                        onSend={(message) => {
                            onSend(message)
                            markDone('set_rate')
                        }}
                    />
                )
            case 'review_pod':
                return (
                    <Button size="sm" variant="secondary" onClick={() => openReview(loadNumber, 'review', loadId)}>
                        {step.label}
                    </Button>
                )
            case 'invoice':
                return null
        }
    }

    return (
        <div className="relative flex items-center justify-end gap-1.5" aria-label={`Fix #${loadNumber}`}>
            {lead}
            {primary && <div key={primary.kind}>{action(primary)}</div>}
            {(behindMenu > 0 || panelOpen) && (
                <Button variant="ghost" size="sm" iconOnly aria-label="More actions" aria-expanded={panelOpen} onClick={() => setMenu(!menu)}>
                    <MoreHorizontal className="h-4 w-4" aria-hidden />
                </Button>
            )}
            {panelOpen && (
                <div className="absolute right-0 top-full z-20 mt-1 flex w-80 max-w-[90vw] flex-col gap-2 rounded-xl bg-surface p-3 text-left shadow-overlay">
                    {revisionOpen && rowRevision.revision ? (
                        <RevisionCard revision={rowRevision.revision} onResolved={resolveRevision} onSubmittingChange={rowRevision.onSubmittingChange} />
                    ) : (
                        actions.map((step) => <div key={step.kind}>{action(step)}</div>)
                    )}
                </div>
            )}
        </div>
    )
}

/**
 * The document slots of a row, from its fixes: a missing rate con or POD opens the row's fix popover (where the upload is), a POD with
 * findings opens the load's invoice review drawer directly. A slot with no fix is a plain chip.
 */
export const useFixSlots = ({ actions, loadNumber, loadId, onOpen }: { actions: RowAction[]; loadNumber: string; loadId?: string; onOpen: () => void }) => {
    const openReview = useInvoiceReview((state) => state.open)
    const rateCon = actions.find((step) => step.kind === 'upload_ratecon')
    const pod = actions.find((step) => step.kind === 'upload_pod')
    const review = actions.find((step) => step.kind === 'review_pod')
    const slots: { rc?: SlotAction; pod?: SlotAction } = {}
    if (rateCon) slots.rc = { label: rateCon.label, onClick: onOpen }
    if (pod) slots.pod = { label: pod.label, onClick: onOpen }
    else if (review) slots.pod = { label: review.label, onClick: () => openReview(loadNumber, 'review', loadId) }
    return slots
}
